import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react'

import { useAuthContext } from 'context/AuthContext'
import { useReportChannel, ReportNotification } from 'hooks/useReportChannel'

export type ReportFilterEntry = {
  key: string
  label: string
  display?: string
  uuid?: string
  amount?: string
  flag?: boolean
}

export type ReportResponse = {
  created_at: Date
  downloaded: boolean
  filters: string
  filter_by: string
  filters_parsed?: ReportFilterEntry[]
  merchant_uuid: string
  model:
    | 'Billing'
    | 'Receivable'
    | 'Customer'
    | 'Transfer'
    | 'Contract'
    | 'Nfse'
    | 'Installment'
    | 'StatementDatePaid'
    | 'StatementExpectedOn'
  status: string
  uuid: string
}

type ReportContextType = {
  reports: ReportResponse[]
  setReports: React.Dispatch<React.SetStateAction<ReportResponse[]>>
  updateReportStatus: (uuid: string, status: string) => void
  lastNotification: ReportNotification | null
  isConnected: boolean
  refreshKey: number
  triggerRefresh: () => void
}

const initialContext: ReportContextType = {
  reports: [],
  setReports: () => null,
  updateReportStatus: () => null,
  lastNotification: null,
  isConnected: false,
  refreshKey: 0,
  triggerRefresh: () => null
}

const ReportContext = createContext<ReportContextType>(initialContext)

export const ReportProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuthContext()
  const [reports, setReports] = useState<ReportResponse[]>([])
  const [lastNotification, setLastNotification] =
    useState<ReportNotification | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  // Use refs to store the latest state without causing re-renders
  const reportsRef = useRef(reports)
  reportsRef.current = reports

  const triggerRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1)
  }, [])

  const updateReportStatus = useCallback((uuid: string, status: string) => {
    setReports((prevReports) =>
      prevReports.map((report) =>
        report.uuid === uuid ? { ...report, status } : report
      )
    )
  }, [])

  // Stable callback that won't change and cause reconnections
  const handleReportUpdate = useCallback((notification: ReportNotification) => {
    setLastNotification(notification)

    // Update the report status in the local state
    setReports((prevReports) =>
      prevReports.map((report) =>
        report.uuid === notification.uuid
          ? { ...report, status: notification.status }
          : report
      )
    )

    // Trigger a refresh to reload data from server
    setRefreshKey((prev) => prev + 1)
  }, [])

  const { isConnected } = useReportChannel({
    merchantUuid: user?.uuid,
    onReportUpdate: handleReportUpdate,
    enabled: !!user?.uuid
  })

  const value = useMemo(
    () => ({
      reports,
      setReports,
      updateReportStatus,
      lastNotification,
      isConnected,
      refreshKey,
      triggerRefresh
    }),
    [
      reports,
      updateReportStatus,
      lastNotification,
      isConnected,
      refreshKey,
      triggerRefresh
    ]
  )

  return (
    <ReportContext.Provider value={value}>{children}</ReportContext.Provider>
  )
}

const useReport = () => {
  const context = useContext(ReportContext)
  if (!context) {
    throw new Error('useReport should be used within a ReportProvider')
  }

  return context
}

export default useReport
